import { useState } from "react";
import { CopyButton } from "../lib/CopyButton";
import { CheckIcon } from "../lib/ui";
import {
  EMBED_LABEL,
  buildEmbedCode,
  cleanAmount,
  cleanPurpose,
  frameSize,
  payerLinkPath,
  renderEmbedPage,
  type EmbedKind,
  type EmbedTheme,
} from "../../shared/embed";
import type { SafeOrg } from "../lib/types";

type Dest = "site" | "doc";
type Ground = "light" | "dark";

function Choice({
  on,
  onClick,
  title,
  desc,
  swatch,
}: {
  on: boolean;
  onClick: () => void;
  title: string;
  desc: string;
  swatch?: React.ReactNode;
}) {
  return (
    <button type="button" className={"choice" + (on ? " choice--on" : "")} aria-pressed={on} onClick={onClick}>
      <span className="tick"><CheckIcon size={12} strokeWidth={2.8} /></span>
      {swatch}
      <span className="ct">{title}</span>
      <span className="cd">{desc}</span>
    </button>
  );
}

/**
 * The dashboard's "Embed a button" tab: pick a button, a style and a destination and get the
 * matching code (an iframe for a website or form builder, an image link for email and READMEs).
 * The preview is the very page that will sit in the iframe, rendered by the same function the
 * /embed route uses.
 */
export default function EmbedTab({
  org,
  origin,
  linkBusy,
  linkError,
  onToggleLink,
  onCopied,
}: {
  org: SafeOrg;
  origin: string;
  linkBusy: boolean;
  linkError: string;
  onToggleLink: (next: boolean) => void;
  /** Reported when any code or address has been copied. */
  onCopied: () => void;
}) {
  const [kind, setKind] = useState<EmbedKind>("pay");
  const [theme, setTheme] = useState<EmbedTheme>("dark");
  const [dest, setDest] = useState<Dest>("site");
  const [amountText, setAmountText] = useState("");
  const [purposeText, setPurposeText] = useState("");
  // Which page the preview sits on. Follows the button style until chosen.
  const [groundChoice, setGroundChoice] = useState<Ground | null>(null);

  const on = org.public_link_enabled;
  const amount = cleanAmount(amountText);
  const amountBad = amountText.trim() !== "" && amount === null;
  const prefill = { amount, purpose: cleanPurpose(purposeText) };
  const ground: Ground = groundChoice ?? (theme === "dark" ? "light" : "dark");

  const code = buildEmbedCode(origin, org.slug, { kind, theme, ...prefill });
  const snippet = dest === "site" ? code.iframe : code.imageHtml;
  const frame = frameSize(kind);
  const previewHtml = renderEmbedPage({
    kind,
    theme,
    view: on ? "live" : "paused",
    href: payerLinkPath(org.slug, prefill),
  });

  return (
    <>
      <p className="eb-intro">
        Your public link is for sending to people. A button is for placing on a page, like a registration form or your
        website, so visitors can pay without hunting for a link.
      </p>

      <div className={"linkstrip" + (on ? "" : " linkstrip--off")} role="group" aria-label="Public link status">
        <div className="switch-row" style={{ alignItems: "flex-start" }}>
          <input
            id="e-switch"
            type="checkbox"
            role="switch"
            className="switch"
            style={{ marginTop: 2 }}
            checked={on}
            disabled={linkBusy}
            onChange={(e) => onToggleLink(e.target.checked)}
          />
          <div>
            <label htmlFor="e-switch">Accepting payments</label>
            <div className={"strip-note" + (on ? "" : " strip-note--off")}>
              {on
                ? "Your public link and every button on your pages are on."
                : "Switched off. Buttons on your pages show “paused” until you switch this back on."}
            </div>
          </div>
        </div>
        <span className="mono strip-url">{`confirmpay.link/${org.slug}`}</span>
      </div>
      {linkError && <div className="error-box" role="alert" style={{ marginTop: -24, marginBottom: 32 }}>{linkError}</div>}

      <div className="eb-grid">
        <div className="eb-steps">
          <section aria-labelledby="eb-s1">
            <div className="stephead"><span className="n">1</span><h2 id="eb-s1">Choose a button</h2></div>
            <div className="choices">
              <Choice on={kind === "pay"} onClick={() => setKind("pay")} title="Pay with ConfirmPay" desc="For fees, tickets, registrations and invoices." />
              <Choice on={kind === "contribute"} onClick={() => setKind("contribute")} title="Contribute with ConfirmPay" desc="For donations and open-ended support." />
            </div>
          </section>

          <section aria-labelledby="eb-s2">
            <div className="stephead"><span className="n">2</span><h2 id="eb-s2">Pick a style</h2></div>
            <div className="choices">
              <Choice
                on={theme === "dark"}
                onClick={() => { setTheme("dark"); setGroundChoice(null); }}
                title="Dark button"
                desc="For light pages."
                swatch={<span className="swatch swatch--dark" />}
              />
              <Choice
                on={theme === "light"}
                onClick={() => { setTheme("light"); setGroundChoice(null); }}
                title="Light button"
                desc="For dark pages."
                swatch={<span className="swatch swatch--light" />}
              />
            </div>

            <div className="choices" style={{ marginTop: 22, gap: 18 }}>
              <div>
                <label className="lbl" htmlFor="eb-amt">Suggested amount <span className="opt">(optional)</span></label>
                <div className="prefix-wrap">
                  <span className="prefix">S$</span>
                  <input
                    id="eb-amt"
                    className="inp inp--mono"
                    inputMode="decimal"
                    placeholder="25.00"
                    value={amountText}
                    aria-invalid={amountBad || undefined}
                    aria-describedby={amountBad ? "eb-amt-hint" : undefined}
                    onChange={(e) => setAmountText(e.target.value.replace(/[^0-9.]/g, "").slice(0, 9))}
                  />
                </div>
                {amountBad && (
                  <p id="eb-amt-hint" className="hint"><span className="bad">Use a number with up to two decimals, like 25 or 25.50.</span></p>
                )}
              </div>
              <div>
                <label className="lbl" htmlFor="eb-for">What it&rsquo;s for <span className="opt">(optional)</span></label>
                <input
                  id="eb-for"
                  className="inp"
                  maxLength={140}
                  placeholder="Registration fee"
                  value={purposeText}
                  onChange={(e) => setPurposeText(e.target.value)}
                />
              </div>
            </div>
            <p className="hint">Visitors see these filled in on the payment page. They can still change them.</p>
          </section>

          <section aria-labelledby="eb-s3">
            <div className="stephead"><span className="n">3</span><h2 id="eb-s3">Where is it going?</h2></div>
            <div className="choices">
              <Choice on={dest === "site"} onClick={() => setDest("site")} title="A website or a form" desc="Any page or form builder with an HTML or embed block." />
              <Choice on={dest === "doc"} onClick={() => setDest("doc")} title="An email, README or document" desc="Anywhere an image that links to a page works, like a GitHub README." />
            </div>

            {dest === "site" ? (
              <>
                <ol className="steps-ol">
                  <li><span className="m">1</span><span>Copy your code.</span></li>
                  <li><span className="m">2</span><span>In your page or form, add a block that accepts HTML or an embed.</span></li>
                  <li><span className="m">3</span><span>Paste it and save. The button shows up where you pasted it.</span></li>
                </ol>
                <p className="hint">
                  If your builder strips embeds and the button doesn&rsquo;t show, choose &ldquo;An email, README or document&rdquo; instead. It works anywhere an image link does.
                </p>
              </>
            ) : (
              <ol className="steps-ol">
                <li><span className="m">1</span><span>Copy your code.</span></li>
                <li><span className="m">2</span><span>Paste it into the HTML or source view of your email, README or document.</span></li>
                <li><span className="m">3</span><span>No HTML view? Add the image, then link it to your public link. Both addresses are under the code.</span></li>
              </ol>
            )}
          </section>
        </div>

        <div className="eb-aside">
          <section className="card eb-preview" aria-labelledby="eb-pv">
            <div className="eb-cardhead">
              <h2 id="eb-pv">Preview</h2>
              <div className="filters" role="group" aria-label="Page behind the button">
                <button type="button" className="fbtn" aria-pressed={ground === "light"} onClick={() => setGroundChoice("light")}>Light page</button>
                <button type="button" className="fbtn" aria-pressed={ground === "dark"} onClick={() => setGroundChoice("dark")}>Dark page</button>
              </div>
            </div>
            <div className={"ground ground--" + ground}>
              <iframe
                title={`Preview of the ${EMBED_LABEL[kind]} button`}
                srcDoc={previewHtml}
                width={frame.width}
                height={frame.height}
                scrolling="no"
                style={{ border: 0, colorScheme: "light" }}
              />
            </div>
            <p className="mono eb-frame">Frame {frame.width} &times; {frame.height} &middot; opens in a new window</p>
          </section>

          <section className="card eb-code" aria-labelledby="eb-cd">
            <div className="eb-cardhead">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <h2 id="eb-cd">Your code</h2>
                <span className="tag">{dest === "site" ? "HTML" : "HTML, image link"}</span>
              </div>
              <CopyButton text={snippet} className="copybtn" onCopied={onCopied}>Copy code</CopyButton>
            </div>
            <pre className="codeblock" tabIndex={0} aria-label="Embed code">{snippet}</pre>

            {dest === "doc" && (
              <div style={{ display: "grid", gap: 14, marginTop: 20 }}>
                <div>
                  <div className="lbl">Image address</div>
                  <div className="copybox copybox--quiet">
                    <span className="mono">{code.imageUrl}</span>
                    <CopyButton text={code.imageUrl} className="copybtn copybtn--quiet" onCopied={onCopied}>Copy</CopyButton>
                  </div>
                </div>
                <div>
                  <div className="lbl">Link it to</div>
                  <div className="copybox copybox--quiet">
                    <span className="mono">{code.linkUrl}</span>
                    <CopyButton text={code.linkUrl} className="copybtn copybtn--quiet" onCopied={onCopied}>Copy</CopyButton>
                  </div>
                </div>
              </div>
            )}

            <p className="hint" style={{ marginTop: 16 }}>
              Switch off &ldquo;Accepting payments&rdquo; and every button pauses by itself. There is nothing to paste again.
              {dest === "doc" && " An image can’t pause itself, so its link shows your payment page’s own notice instead."}
            </p>
          </section>
        </div>
      </div>
    </>
  );
}
